import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import reduxStore from "reduxstore/reduxStore";
import i18n from "i18n/i18n";
import { ReduxState } from "helpers/helper";
import isEmpty from "lodash/isEmpty";
import { HEADOFFICE_OPTION_VALUE } from "constants/AppConstants";
import { setCurrentUser } from "actions/authActions";
import { setDocDivisionAccess } from "./loginUtils";
import { Modal, SearchSuggestionSelect, Icons } from "@texada/ui";

const DocDivisionAccessModal = ({ isOpen, onClose }) => {
  const isLoggedIn = useSelector<ReduxState, boolean[]>(
    state => state.auth.isAuthenticated
  );
  const user = useSelector<ReduxState, any>(state => state?.auth?.user);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedDivision, setSelectedDivision] = useState({});
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isLoggedIn || !isOpen) return;

    fetchDivisions();
    fetchPreSelected();
  }, [isLoggedIn, isOpen, user]);

  const fetchDivisions = () => {
    let userDivisions = [
      ...(user?.login_divisions || []),
      ...(user?.divisions || [])
    ];
    if (user?.upgraded) {
      userDivisions = user?.divisions?.filter(e => e.is_user_division);
    }
    const newDivisionList = userDivisions.map(division => ({
      label: division.warehouse.title,
      value: division.warehouse.srm_uid || HEADOFFICE_OPTION_VALUE,
      srm_uid: division.warehouse.srm_uid
    }));
    const uniqueDivisions = [
      ...new Map(newDivisionList.map(item => [item.srm_uid, item])).values()
    ];
    setDivisionOptions(uniqueDivisions);

    setIsModalOpen(true);
  };

  const fetchPreSelected = () => {
    setSearch("");
    setSelectedDivision([]);
    const docAccess = JSON.parse(localStorage.getItem("docDivisionAccess"));

    if (isEmpty(docAccess?.[user.tenant_id])) return;

    const docDivision = docAccess[user.tenant_id].divisions.find(
      e => e.is_doc_division
    );
    const target = [
      ...(user?.login_divisions || []),
      ...(user?.divisions || [])
    ]?.find(e => e.warehouse.srm_uid === docDivision.srm_uid);

    if (!target) return;

    setSelectedDivision({
      srm_uid: target.warehouse.srm_uid,
      label: target.warehouse.title,
      value: target.warehouse.srm_uid || HEADOFFICE_OPTION_VALUE
    });
    setSearch(target.warehouse.title);
  };

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const userInfo = await setDocDivisionAccess(
        user,
        selectedDivision.srm_uid || HEADOFFICE_OPTION_VALUE
      );
      await reduxStore.dispatch(setCurrentUser(userInfo));
      setIsLoading(false);
      setIsModalOpen(false);
      onClose();
    } catch (err) {
      setIsLoading(false);
      setIsModalOpen(false);
    }
  };

  return (
    <Modal
      size="sm"
      isOpen={isModalOpen}
      isLoading={isLoading}
      isSubmitting={isLoading}
      header={i18n.t("document_division")}
    >
      <Modal.Body>
        <SearchSuggestionSelect
          required
          label={i18n.t("division")}
          placeholder={i18n.t("select_document_division")}
          inputValue={search}
          onInputValueChange={setSearch}
          options={divisionOptions}
          selectedItem={selectedDivision?.value}
          onChange={setSelectedDivision}
          error={errors?.division}
        />
      </Modal.Body>
      <Modal.Footer
        saveButton
        saveButtonText={i18n.t("confirm")}
        saveButtonProps={{ icon: { render: Icons.Check } }}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default DocDivisionAccessModal;
